import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CamionMuelle, SlotMuelle } from '@/types'
import type { DockTrack } from '@/utils/dockTimeTracks'
import { tramoDe } from '@/utils/camionesMuelle'
import { INFO_COL_DOCK } from '@/config/dockSchedule'

/**
 * Cuántos muelles están ocupados en cada tramo, sumando todos.
 *
 * Es la fila que responde de un vistazo a la pregunta que el cuadro no contesta
 * mirando muelle por muelle: **dónde está el cuello de botella**. Un almacén con
 * cuatro muelles y cuatro camiones a las diez de la mañana está lleno aunque
 * cada fila individual parezca tranquila.
 *
 * La escala es la ocupación relativa al número de muelles, no un absoluto: dos
 * camiones son la mitad del almacén si hay cuatro muelles y una anécdota si hay
 * veinte.
 */
export function FranjaOcupacion({ camiones, muelles, track, dark }: {
  camiones: CamionMuelle[]
  muelles: SlotMuelle[]
  track: DockTrack
  dark: boolean
}) {
  const { t } = useTranslation()

  /** Un tramo por media hora del eje, con cuántos muelles distintos hay ocupados. */
  const tramos = useMemo(() => {
    const iniMs = track.rangeStart.getTime()
    const finMs = track.rangeEnd.getTime()
    const paso = 30 * 60_000
    // El eje de la semana va a trozos, así que no se puede recorrer linealmente:
    // se recorre y se descarta lo que caiga en una noche, que `pos` devuelve null.
    const salida: { left: number; width: number; ocupados: number; desde: Date }[] = []

    const ocupacionEn = (ms: number) => {
      const muellesOcupados = new Set<string>()
      for (const c of camiones) {
        const tr = tramoDe(c)
        if (tr && ms >= tr.iniMs && ms < tr.finMs && c.muelle) muellesOcupados.add(c.muelle)
      }
      return muellesOcupados.size
    }

    for (let ms = iniMs; ms < finMs; ms += paso) {
      const p = track.pos(new Date(ms), new Date(ms + paso))
      if (!p) continue
      salida.push({ left: p.left, width: p.width, ocupados: ocupacionEn(ms), desde: new Date(ms) })
    }
    return salida
  }, [camiones, track])

  const total = Math.max(1, muelles.length)
  const pico = Math.max(0, ...tramos.map((x) => x.ocupados))

  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const txtMuted = dark ? 'text-slate-400' : 'text-slate-500'

  /** Cuatro escalones y no un degradado continuo: el ojo distingue cuatro
   *  intensidades de un vistazo, dieciséis no. */
  const tono = (ocupados: number) => {
    if (ocupados === 0) return dark ? 'bg-white/5' : 'bg-slate-100'
    const r = ocupados / total
    if (r >= 0.99) return 'bg-rose-500'
    if (r >= 0.66) return 'bg-amber-500'
    if (r >= 0.33) return 'bg-sky-500'
    return dark ? 'bg-sky-500/40' : 'bg-sky-300'
  }

  return (
    <div className={`flex border-b ${bdr} ${dark ? 'bg-slate-900/60' : 'bg-slate-50/70'}`}>
      <div
        className={`shrink-0 px-3 py-1.5 border-r ${bdr} flex items-center justify-between gap-2`}
        style={{ width: INFO_COL_DOCK }}
      >
        <span className={`text-[10px] font-bold uppercase tracking-wide ${txtMuted}`}>
          {t('muelles.franja.titulo')}
        </span>
        <span className={`text-[10px] tabular-nums ${txtMuted}`}>
          {t('muelles.franja.pico', { n: pico, total })}
        </span>
      </div>

      <div className="flex-1 relative min-w-0 h-7">
        {tramos.map((x, i) => (
          <div
            key={i}
            className={`absolute top-1.5 bottom-1.5 rounded-[2px] ${tono(x.ocupados)}`}
            style={{ left: `${x.left}%`, width: `calc(${x.width}% - 1px)` }}
            title={t('muelles.franja.tramo', {
              hora: `${String(x.desde.getHours()).padStart(2, '0')}:${String(x.desde.getMinutes()).padStart(2, '0')}`,
              n: x.ocupados,
              total,
            })}
          />
        ))}

        {/* La línea de ahora también cruza la franja: sin ella, la franja y la
            rejilla parecen dos cosas distintas. */}
        {track.todayX !== null && (
          <div
            className="absolute top-0 bottom-0 pointer-events-none z-10"
            style={{
              left: `${track.todayX}%`,
              width: 1.5,
              backgroundColor: dark ? 'rgba(248,113,113,0.55)' : 'rgba(239,68,68,0.40)',
            }}
          />
        )}
      </div>
    </div>
  )
}
